import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@stockline/ui/components/popover";
import { Button } from "@stockline/ui/components/button";
import { XCircle, RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Props {
  message: string;
  onRetry: () => void;
  isRetrying: boolean;
  children: React.ReactNode;
}

/** Popover showing the last ERP modify error + a retry button, triggered
 * by the failed modify button. Mirrors ExportErrorPopover for consistency. */
export function ModifyErrorPopover({
  message,
  onRetry,
  isRetrying,
  children,
}: Readonly<Props>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="w-80 p-3"
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-2">
          <XCircle className="h-4 w-4 text-red-500 shrink-0" />
          <span className="text-sm font-semibold text-red-700">
            {t("ordersRecap.modifyErrorTitle")}
          </span>
        </div>

        <div className="text-xs bg-red-50 rounded px-2 py-1.5 border border-red-100 mb-3 max-h-40 overflow-y-auto whitespace-pre-wrap text-red-700">
          {message}
        </div>

        <Button
          variant="outline"
          size="sm"
          className="w-full h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
          onClick={(e) => {
            e.stopPropagation();
            onRetry();
            setOpen(false);
          }}
          disabled={isRetrying}
        >
          <RotateCcw className="h-3 w-3 mr-1.5" />
          {t("ordersRecap.modifyRetry")}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
